import { LoadingOutlined } from '@ant-design/icons'
import { DrilldownTypeSelect } from 'features/drilldown/components/fieldSelect'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import {
  DrilldownFieldType,
  DrilldownRolodexType,
} from 'features/drilldown/rolodex/ontology/ontologyTypes'
import _ from 'lodash'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GenericPanelData } from 'types'

import {
  ActionSection,
  ConfirmationModal,
  EmptyPreviewState,
  PreviewStep,
  PropertySelectionStep,
  StepCard,
  ValueConfigurationStep,
} from './components'
import { EntriesPreview } from './components/EntriesPreview'
import {
  useBulkEditActions,
  useBulkEditData,
  useBulkEditState,
  useFieldType,
} from './hooks'

const PanelContainer = styled.div`
  padding: 1.5rem;
  width: 100%;
  height: 100%;
  min-height: 800px;
`

const LeftColumn = styled.div`
  min-width: 350px;
  flex: 1 1 300px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const RightColumn = styled.div`
  min-width: 400px;
  flex: 3 1 500px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
`

const FlexRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  width: 100%;
`

const LoadingSpinnerContainer = styled.div`
  text-align: center;
  padding: 2rem;
`

const LoadingSpinnerIcon = styled(LoadingOutlined)`
  font-size: 2rem;
`

export interface BulkEditPanelData extends GenericPanelData {
  selectedType: DrilldownRolodexType
  selectedField: string
  customPropertyName: string
  customPropertyType: DrilldownFieldType
  prefillValue: string
  isEditingCustomProperty: boolean
  shouldPrefillValues: boolean
}

export const LATEST_BULK_EDIT_PANEL_DATA_SCHEMA_VERSION = 1

export const DEFAULT_BULK_EDIT_PANEL_DATA: BulkEditPanelData = {
  selectedType: '',
  selectedField: '',
  customPropertyName: '',
  customPropertyType: 'text',
  prefillValue: '',
  isEditingCustomProperty: false,
  shouldPrefillValues: false,
  panelDataSchemaVersion: LATEST_BULK_EDIT_PANEL_DATA_SCHEMA_VERSION,
  title: 'Bulk Edit',
  isPinned: false,
}

export const BulkEditPanel: FC = () => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<BulkEditPanelData>()

  const {
    isSubmitting,
    isModalVisible,
    editedValues,
    propertyKey,
    setSelectedType,
    setCustomPropertyName,
    setCustomPropertyType,
    setShouldPrefillValues,
    setPrefillValue,
    handlePropertySelectionChange,
    handleValueChange,
    resetState,
    setIsSubmitting,
    setIsModalVisible,
  } = useBulkEditState(panelData, setPanelData)

  const {
    filteredIds,
    totalCount,
    isLoadingIds,
    isLoadingCount,
    isLoadingEntries,
    isWithinEntryCountLimit,
    numEntriesToUpdate,
    entriesWithEdits,
    forceRefresh,
  } = useBulkEditData({
    selectedType: panelData.selectedType,
    propertyKey,
    editedValues,
    shouldPrefillValues: panelData.shouldPrefillValues,
    prefillValue: panelData.prefillValue,
  })

  const selectedFieldType = useFieldType({
    isEditingCustomProperty: panelData.isEditingCustomProperty,
    customPropertyType: panelData.customPropertyType,
    selectedField: panelData.selectedField,
  })

  // Actions
  const { handleBulkEditSubmit, handleModalConfirm, handleModalCancel } =
    useBulkEditActions({
      panelData,
      entriesWithEdits,
      totalCount,
      propertyKey,
      selectedFieldType,
      forceRefresh,
      resetState,
      setIsSubmitting,
      setIsModalVisible,
    })

  // Future feature idea: make this depend on individual input field validation
  const shouldAllowSubmission = useMemo(() => {
    const hasCustomPropertyErrors =
      panelData.isEditingCustomProperty && _.isNil(panelData.customPropertyName)

    const hasTypeAndFieldErrors =
      _.isNil(panelData.selectedType) ||
      (!panelData.isEditingCustomProperty && _.isNil(panelData.selectedField))

    return (
      !hasCustomPropertyErrors &&
      !hasTypeAndFieldErrors &&
      isWithinEntryCountLimit
    )
  }, [
    panelData.selectedType,
    isWithinEntryCountLimit,
    panelData.selectedField,
    panelData.isEditingCustomProperty,
    panelData.customPropertyName,
  ])

  const showPreviewStep =
    panelData.selectedType &&
    isWithinEntryCountLimit &&
    (panelData.selectedField ||
      (panelData.isEditingCustomProperty && panelData.customPropertyName)) &&
    entriesWithEdits.length > 0

  return (
    <PanelContainer>
      <ConfirmationModal
        isVisible={isModalVisible}
        isSubmitting={isSubmitting}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />

      <FlexRow>
        <LeftColumn>
          <StepCard
            stepNumber={1}
            title='Select Entry Type'
            description='Choose the type of entries you want to edit'>
            <DrilldownTypeSelect
              selectedType={panelData.selectedType}
              setSelectedType={(value) =>
                setSelectedType(value as DrilldownRolodexType)
              }
              placeholder='Choose a type to edit'
            />
          </StepCard>

          {panelData.selectedType && (
            <StepCard
              stepNumber={2}
              title='Preview Entries'
              description='Review the entries that will be updated. These are all entries for the selected type in the current site, adjusted for any filters earlier in the exploration.'>
              {isLoadingIds || isLoadingCount ? (
                <LoadingSpinnerContainer>
                  <LoadingSpinnerIcon />
                  <div style={{ marginTop: '1rem' }}>Loading entries...</div>
                </LoadingSpinnerContainer>
              ) : (
                <EntriesPreview
                  filteredIds={filteredIds}
                  totalCount={totalCount}
                  isWithinEntryLimit={isWithinEntryCountLimit}
                />
              )}
            </StepCard>
          )}

          {panelData.selectedType && isWithinEntryCountLimit && (
            <StepCard
              stepNumber={3}
              title='Select Property'
              description='Choose which property to update or create a custom one'>
              <PropertySelectionStep
                selectedType={panelData.selectedType}
                isWithinEntryCountLimit={isWithinEntryCountLimit}
                isEditingCustomProperty={panelData.isEditingCustomProperty}
                selectedField={panelData.selectedField}
                customPropertyName={panelData.customPropertyName}
                customPropertyType={panelData.customPropertyType}
                onPropertySelectionChange={handlePropertySelectionChange}
                onCustomPropertyNameChange={(value) =>
                  setCustomPropertyName(value)
                }
                onCustomPropertyTypeChange={(value) =>
                  setCustomPropertyType(value)
                }
              />
            </StepCard>
          )}
        </LeftColumn>

        <RightColumn>
          {panelData.selectedType &&
            isWithinEntryCountLimit &&
            (panelData.selectedField ||
              (panelData.isEditingCustomProperty &&
                panelData.customPropertyName)) && (
              <StepCard
                stepNumber={4}
                title='Configure Values'
                description='Configure how values will be set for the selected property'>
                <ValueConfigurationStep
                  selectedType={panelData.selectedType}
                  isWithinEntryCountLimit={isWithinEntryCountLimit}
                  selectedField={panelData.selectedField}
                  isEditingCustomProperty={panelData.isEditingCustomProperty}
                  customPropertyName={panelData.customPropertyName}
                  customPropertyType={panelData.customPropertyType}
                  shouldPrefillValues={panelData.shouldPrefillValues}
                  prefillValue={panelData.prefillValue}
                  onShouldPrefillValuesChange={(value) =>
                    setShouldPrefillValues(value)
                  }
                  onPrefillValueChange={(value) => setPrefillValue(value)}
                />
              </StepCard>
            )}
          {showPreviewStep ? (
            <>
              <StepCard
                stepNumber={5}
                title='Preview Changes'
                description={
                  panelData.shouldPrefillValues
                    ? 'Review and edit individual values as needed. You can modify any value in the "New Value" column before submitting.'
                    : 'Edit individual values as needed. Only entries with changes will be updated when you submit.'
                }>
                <PreviewStep
                  entriesWithEdits={entriesWithEdits}
                  isLoadingEntries={isLoadingEntries}
                  onValueChange={handleValueChange}
                  fieldType={selectedFieldType}
                  fieldKey={panelData.selectedField}
                />
              </StepCard>
              <ActionSection
                shouldAllowSubmission={shouldAllowSubmission}
                isSubmitting={isSubmitting}
                numEntriesToUpdate={numEntriesToUpdate}
                selectedType={panelData.selectedType}
                onBulkEdit={handleBulkEditSubmit}
              />
            </>
          ) : (
            <EmptyPreviewState />
          )}
        </RightColumn>
      </FlexRow>
    </PanelContainer>
  )
}
